import { useMemo } from "react"

import { keepPreviousData, useQuery } from "@tanstack/react-query"
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table"
import { DateTime } from "luxon"

import { getKPIRequest } from "@/api/getKPIRequest.ts"
import type { KPI, KPIGroupRequest } from "@/types/kpiTypes.ts"
import { atTarget } from "@/util/atTarget.ts"
import { getDates } from "@/util/getDates.ts"
import { numberFmt } from "@/util/numberFmt.ts"

import { Skeleton } from "./ui/skeleton.tsx"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table.tsx"

const columnHelper = createColumnHelper<KPI>()

export default function KPITable({
  request,
  showAverage,
  title,
  paddingArray,
  hideNames,
  refetchInterval,
}: {
  request: KPIGroupRequest
  showAverage: boolean
  title?: string
  paddingArray?: number[]
  hideNames?: boolean
  refetchInterval?: number
}) {
  const { data, isPending, isError } = useQuery({
    queryKey: ["kpiTable", { request }],
    queryFn: () => getKPIRequest(request),
    placeholderData: keepPreviousData,
    refetchInterval,
  })

  const dates = useMemo(
    () => getDates(request, request.asOf || DateTime.now().toUTC().startOf("day").toJSDate()) || [],
    [request],
  )

  const columnDefs = useMemo(() => {
    const nameCol = columnHelper.accessor((kpi) => kpi.id, {
      id: "names",
      header: () => (
        <div className="sm:w-[225px] font-semibold p-2 text-xs sm:text-sm sticky left-0" key="kpi">
          {title ? title : "KPIs"}
        </div>
      ),
      cell: ({ row }) => (
        <>
          <div
            className="font-semibold sm:w-[225px] hidden sm:block sticky left-0"
            style={{
              paddingLeft: paddingArray ? `${paddingArray[row.index]}px` : "8px",
            }}
          >
            {row.depth == 0 ? (
              <>
                {row.original.name} <i>({row.original.unit})</i>{" "}
              </>
            ) : (
              <span className="pl-5">{row.original.name}</span>
            )}
          </div>
          <div className="font-semibold text-xs pl-2 sm:hidden">
            <span>{row.original.name}</span>
          </div>
        </>
      ),
    })
    const cols = dates.map((date) =>
      columnHelper.accessor(
        (kpi) => {
          if (kpi.values?.findIndex((v) => v.type == "Actual") == -1) {
            return kpi.values?.find((v) => v.date == date)?.value
          } else {
            return kpi.values?.find((v) => v.date == date && v.type == "Actual")?.value
          }
        },
        {
          id: date,
          header: () => (
            <div className="text-xs sm:text-sm">
              <span className="hidden sm:inline">
                {DateTime.fromISO(date).toFormat(request.aggType == "Monthly" ? "LLL-yy" : "L/d")}
              </span>
              <span className="sm:hidden">
                {DateTime.fromISO(date).toFormat(request.aggType == "Monthly" ? "LLL" : "L/d")}
              </span>
            </div>
          ),
          cell: (context) => {
            const target = atTarget(context.row.original, date)

            return (
              <div
                className={`text-xs sm:text-sm ${
                  context.getValue() === undefined && "text-muted-foreground italic "
                } ${
                  target !== undefined &&
                  (target ? "text-status-good font-semibold" : "text-status-bad font-semibold")
                }`}
              >
                {context.getValue() === undefined
                  ? "N/A"
                  : `${context.row.original.unit == "$" && (context.getValue() || 0) > 0 ? "$" : ""}${context.row.original.unit == "$" && (context.getValue() || 0) == 0 ? "—" : numberFmt(context.getValue() || 0)}${
                      context.row.original.unit == "%" ? "%" : ""
                    }`}
              </div>
            )
          },
        },
      ),
    )

    const avgCol = columnHelper.accessor(
      (kpi) => {
        const filtered =
          kpi.values?.filter(
            (v) => (v.type == "Actual" || v.type == "Mixed") && dates.includes(v.date),
          ) || []
        const total = filtered.reduce((a, c) => a + c.value, 0)
        const count = filtered.length

        return count == 0 ? 0 : total / count
      },
      {
        id: "average",
        header: () => <div className="p-2">AVG</div>,
        cell: (context) => (
          <div className="pl-2">{`${numberFmt(context.getValue() || 0)}${
            context.row.original.unit == "%" ? "%" : ""
          }`}</div>
        ),
      },
    )
    if (showAverage) {
      if (hideNames) {
        return [...cols, avgCol]
      }
      return [nameCol, ...cols, avgCol]
    } else {
      if (hideNames) {
        return cols
      }
      return [nameCol, ...cols]
    }
  }, [dates, showAverage, title, hideNames, paddingArray, request.aggType])

  const table = useReactTable<KPI>({
    data: data || [],
    columns: columnDefs,
    getCoreRowModel: getCoreRowModel(),
  })

  if (isError)
    return (
      <div
        className="w-full border p-4 shadow-sm rounded-md"
        style={{ height: `${request.kpis.length * 53 + 52.5}px` }}
      >
        <span className="text-status-bad font-semibold">Network Error</span>
      </div>
    )

  return (
    <Table>
      {table.getHeaderGroups().map((hg, i) => (
        <TableHeader className="bg-card-header border-b" key={i}>
          <TableRow
            className="
                  [&_th:first-child]:rounded-tl-md
                  [&_th:last-child]:rounded-tr-md
                "
          >
            {hg.headers.map((header, i) => (
              <TableHead className="p-2" key={i}>
                <>{flexRender(header.column.columnDef.header, header.getContext())}</>
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
      ))}
      <TableBody>
        {isPending
          ? request.kpis.map((_, i) => (
              <TableRow key={i}>
                <TableCell colSpan={dates.length + (showAverage ? 2 : 1)}>
                  <Skeleton className="w-full h-[36.5px]" />
                </TableCell>
              </TableRow>
            ))
          : table.getRowModel().rows.map((row) => (
              <TableRow key={row.id}>
                {row.getVisibleCells().map((cell, i) => (
                  <TableCell key={i} className="py-4">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))}
      </TableBody>
    </Table>
  )
}
